import React, { useRef, useState } from 'react';
import { DatagridConfigurable, FunctionField, NumberField, TextField } from 'react-admin';
import RaList from '../../components/ra-list/RaList';
import RenderControls, { ControlConfig } from '../../components/ra-list/RenderControls';
import { FilterListProvider } from '../../context/FilterListProvider';
import { formatDateTime } from '../../components/ex_dayjs';
import { Box, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';


/** A selectable option used by the market-type and event-type filters. */
interface Choice {
    id: string;
    name: string;
}

/** Props accepted by {@link EventLevelView}. */
interface EventLevelViewProps {
    /** Market-type choices loaded from the server and shared with the venue-level tab. */
    marketTypeChoices?: Choice[];
}

/**
 * GA/Reserved toggle options shown in the Type filter.
 * The empty-string entry means "All types".
 */
const TYPE_CHOICES: Choice[] = [
    { id: '', name: 'All' },
    { id: 'GA', name: 'GA' },
    { id: 'Reserved', name: 'Reserved' },
];

const DEFAULT_HOURS = 24;

/**
 * Event-Level tab of the Inventory Drop Analysis page.
 *
 * Renders a filterable, paginated data grid of {@link EventStat} records backed
 * by the `EventLevel` OData controller.  Filter controls follow the same
 * `RaList` / `RenderControls` pattern used by {@link InventoryDataGrid}.
 *
 * **Filter controls**
 * - Market Type (select)
 * - Hours window (number, default {@link DEFAULT_HOURS})
 * - Venue name (text)
 * - VenueId (number)
 * - SiteId (text)
 * - Event Name (text)
 * - GA / Reserved (toggle — uses {@link MToggleButtonGroup})
 *
 * **Grid columns**
 * EventName (hyperlink), VenueName, EventDate, Type,
 * Current (24h Ago), 48h Ago, 72h Ago, 7 Days Ago, 14 Days Ago, 1 Month Ago, Latest Record
 *
 * @param props - {@link EventLevelViewProps}
 */
const EventLevelView: React.FC<EventLevelViewProps> = ({ marketTypeChoices }) => {
    const refbtnFilter = useRef<HTMLButtonElement>(null);

    const [defaultValues] = useState({
        marketType_eq: '',
        hours: DEFAULT_HOURS,
        venueName: '',
        type_eq: '',
    });

    /** Control definitions consumed by {@link RenderControls}. */
    const controls: ControlConfig[] = [
        {
            component: 'select',
            name: 'marketType_eq',
            label: 'Market Type',
            choices: marketTypeChoices || [],
            xs: 1.5,
        },
        {
            component: 'number',
            name: 'hours',
            label: 'Hours (Drop Window)',
            xs: 1,
        },
        {
            component: 'text',
            name: 'id_eq',
            label: 'SiteId',
            xs: 1.5,
        },
        {
            component: 'text',
            name: 'eventName_contains',
            label: 'Event Name',
            xs: 2
        },
        {
            component: 'number',
            name: 'venueId_eq',
            label: 'VenueId',
            xs: 1.2,
        },
        {
            component: 'text',
            name: 'venueName_contains',
            label: 'Venue',
            xs: 2,
        },
        {
            component: 'toggle',
            name: 'type_eq',
            label: 'Type',
            choices: TYPE_CHOICES,
            xs: 1.2,
        },
    ];

    return (
        <RaList
            resource="EventLevel"
            title="Event-Level Inventory"
            sort={{ field: 'latestRecord', order: 'DESC' }}
            perPage={100}
            pagination={[100, 500, 1000]}
            filterDefaultValues={defaultValues}
            filters={
                <FilterListProvider>
                    <RenderControls
                        refFilter={refbtnFilter}
                        controls={controls}
                        defaultValues={defaultValues}
                        exportFile={{
                            columns: [
                                'MarketType', 'Id', 'Url', 'EventName', 'VenueId', 'VenueName', 'EventDate', 'Type',
                                'LatestRecord', 'CurrentCount', 'Prev48hCount', 'Prev72hCount',
                                'OneWeekAgoCount', 'TwoWeeksAgoCount', 'MonthAgoCount',
                            ],
                            filename: 'event-level-export'
                        }}
                    />
                </FilterListProvider>
            }
        >
            <DatagridConfigurable bulkActionButtons={false}>
                <TextField  source="marketType" label="Market Type" />
                <TextField  source="id" label="SiteId" />
                {/* EventName rendered as a hyperlink when a URL is available */}
                <FunctionField
                    source="eventName"
                    label="Event Name"
                    render={(record: Record<string, unknown>) => {
                        const url  = record['url']       as string | undefined;
                        const name = record['eventName'] as string | undefined;
                        return url ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                {name}
                            </a>
                        ) : (
                            name || '-'
                        );
                    }}
                />
                <TextField  source="venueId"          label="VenueId" />
                <TextField  source="venueName"        label="Venue Name" />
                <FunctionField label="Event Date"     source="eventDate" render={record => record.eventDate && formatDateTime(record.eventDate)} />
                <TextField  source="type"             label="Type" />
                <NumberField source="latestRecord"    label={
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <span>Latest</span>
                        <Tooltip title="Latest(24h/48h/72h/7d)" placement="top">
                            <InfoOutlinedIcon fontSize="inherit" />
                        </Tooltip>
                    </Box>
                } />
                <NumberField source="currentCount"    label="24h Ago" />
                <NumberField source="prev48hCount"    label="48h Ago" />
                <NumberField source="prev72hCount"    label="72h Ago" />
                <NumberField source="oneWeekAgoCount" label="7 Days Ago" />
                <NumberField source="twoWeeksAgoCount" label="14 Days Ago" />
                <NumberField source="monthAgoCount"   label="1 Month Ago" />
            </DatagridConfigurable>
        </RaList>
    );
};

export default EventLevelView;
